import type Lenis from 'lenis'

import { prefersReducedMotion } from '@/lib/motion'

type ScrollOptions = {
    immediate?: boolean
    offset?: number
    focus?: boolean
}

let lenisInstance: Lenis | null = null

export const setLenis = (instance: Lenis | null): void => {
    lenisInstance = instance
}

export const getLenis = (): Lenis | null => lenisInstance

const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4)

const moveFocus = (target: HTMLElement): void => {
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
    target.focus({ preventScroll: true })
}

/** Scrolls to an element (or a pixel offset) through Lenis when it runs, natively otherwise. */
export const scrollToTarget = (target: HTMLElement | number, options: ScrollOptions = {}): void => {
    const { immediate = false, offset = 0, focus = false } = options
    const lenis = getLenis()

    if (lenis) {
        lenis.scrollTo(target, {
            offset,
            immediate,
            duration: 1.5,
            easing: easeOutQuart,
            onComplete: () => {
                if (focus && typeof target !== 'number') moveFocus(target)
            }
        })

        return
    }

    const behavior: ScrollBehavior = immediate || prefersReducedMotion() ? 'auto' : 'smooth'

    if (typeof target === 'number') {
        window.scrollTo({ top: target + offset, behavior })

        return
    }

    const top = target.getBoundingClientRect().top + window.scrollY + offset

    window.scrollTo({ top, behavior })

    if (focus) moveFocus(target)
}

export const scrollToHash = (hash: string, options: ScrollOptions = {}): boolean => {
    const id = decodeURIComponent(hash.replace(/^#/, ''))

    if (!id) return false

    const target = document.getElementById(id)

    if (!target) return false

    scrollToTarget(target, options)

    return true
}
